import { useState } from 'react';
import { Loader2, HelpCircle } from 'lucide-react';
import { aiQuestions } from '@/lib/aiApi';

export default function QuestionsTab({ file, documentHash }) {
  const [topic, setTopic] = useState('');
  const [format, setFormat] = useState('short_answer');
  const [count, setCount] = useState(8);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const data = await aiQuestions(documentHash
        ? { documentHash, topic, count, format }
        : { file, topic, count, format });
      setResult(data.questions || []);
    } catch (err) {
      setError(err.message || 'Question generation failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-4 overflow-y-auto h-full">
      <div className="space-y-3">
        <label className="block text-sm">
          <span className="text-gray-700 font-medium">Focus topic (optional)</span>
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. chapter 3, financial terms"
            className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
          />
        </label>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label>
            <span className="text-gray-700 font-medium">Format</span>
            <select value={format} onChange={(e) => setFormat(e.target.value)} className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm">
              <option value="short_answer">Short answer</option>
              <option value="multiple_choice">Multiple choice</option>
              <option value="true_false">True / False</option>
            </select>
          </label>
          <label>
            <span className="text-gray-700 font-medium">Count</span>
            <input
              type="number"
              min="1"
              max="20"
              value={count}
              onChange={(e) => setCount(Math.min(20, Math.max(1, Number(e.target.value) || 1)))}
              className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
            />
          </label>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={busy}
          className="w-full rounded-lg bg-indigo-600 text-white py-2 text-sm font-semibold hover:bg-indigo-700 disabled:bg-indigo-300 inline-flex items-center justify-center gap-1"
        >
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><HelpCircle className="w-4 h-4" /> Generate questions</>}
        </button>
        {error && <div className="text-sm text-red-600">{error}</div>}
        {result && (
          <ol className="space-y-3 list-decimal pl-5">
            {result.map((q, i) => (
              <li key={i} className="text-sm">
                <div className="font-medium text-gray-900">{q.question}</div>
                {q.options && q.options.length > 0 && (
                  <ul className="mt-1 ml-2 text-xs text-gray-700 space-y-0.5">
                    {q.options.map((o, j) => <li key={j}>{String.fromCharCode(97 + j)}. {o}</li>)}
                  </ul>
                )}
                <details className="mt-1 text-xs text-gray-600">
                  <summary className="cursor-pointer text-indigo-600">Show answer</summary>
                  <div className="mt-1"><b>Answer:</b> {q.answer}</div>
                  {q.explanation && <div><b>Explanation:</b> {q.explanation}</div>}
                  {q.pageRef && <div className="text-gray-500">Page {q.pageRef}</div>}
                </details>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
